// ════════════════════════════════════════════════════════════
//  ui/Climb.jsx — THE CLIMB's screen (SCAFFOLD 2026-10-18: proves the seams;
//  the look and the words are placeholders caP'n will replace)
// ════════════════════════════════════════════════════════════
//  One quiet page: your rating and where it would sit in the League, the
//  ladder, and — while a run is on — its totem and what your three carry.
//  Detail stays one tap away (the Empire never drowns you in numbers).
//  The rules are climb/climb.js; this file only shows them and asks App to fight.
// ════════════════════════════════════════════════════════════
import { useEffect, useMemo, useRef, useState } from "react";
import "./climb.css";
import { LINES, portraitUrl } from "../data/roster.js";
import { useCircuit } from "./circuitStore.js";
import { GENESIS_LEAGUE, BEINGS, statOf, rankKeys, ELO_START } from "../online/circuit.js";
import { ladderOf, startRun, boutOf, openRungs, wouldSit, abandon, isCleared, CLIMB_RUNG_PURSE } from "../climb/climb.js";
import { loadClimb, watchClimb, liveState, climbUid, claimBout, beginRun, giveUpRun, endWalkedOut, LIVE_STALE_MS } from "../climb/climbStore.js";
import { fmtKrests } from "../online/ie.js";

/** The League as it stands right now. ONE snapshot: the poller stops once it has the standings
 *  (caP'n's "does it drain": the Climb never needs the 8-second refresh the League board uses). */
export function useClimbLeague() {
  const [got, setGot] = useState(false);
  const C = useCircuit(!got);
  useEffect(() => { if (C.ready) setGot(true); }, [C.ready]);
  return useMemo(() => {
    const league = (C.ready && C.league) || GENESIS_LEAGUE;
    const S = C.S || new Map();
    const eloOf = (k) => (C.ready ? statOf(S, k).elo : ELO_START);
    const number1 = C.ready ? rankKeys(league.circuit || [], S)[0] : null;
    const minds = C.minds && C.minds.n;
    // who they are right now: this week's playbook (mind.js) — their three and their style
    const entrantOf = (k) => {
      const b = BEINGS.get(k); if (!b) return null;
      const m = minds && minds[k];
      return { key: k, name: b.name, team: m && Array.isArray(m.t) && m.t.length === 3 ? m.t : b.team, style: (m && m.s) || null };
    };
    const ladder = ladderOf({ league, eloOf, number1 });
    const allElos = [...(league.circuit || []), ...(league.contenders || [])].map(eloOf);
    return { ready: !!C.ready, ladder, eloOf, number1, entrantOf, allElos };
  }, [C]);
}

const Faces = ({ team, carry = null }) => (
  <span className="cl-faces">
    {team.map((l, i) => {
      const out = carry && carry[i] <= 0;
      return (
        <span key={i} className={"cl-face" + (out ? " out" : "")} title={`${LINES[l] ? LINES[l].name : l}${carry ? (out ? " — ruined (one resurrection can bring it back)" : ` — ${carry[i]} Anima`) : ""}`}>
          <img src={portraitUrl(l, "beb")} alt="" loading="lazy" />
          {carry && <i>{out ? "✕" : carry[i]}</i>}
        </span>
      );
    })}
  </span>
);

export default function ClimbScreen({ on, team, tomes, news, onNews, onFight, onPickTeam, onBack }) {
  const uid = climbUid(on);                          // a real sign-in saves to the server; a guest pass, this browser
  const L = useClimbLeague();
  const [state, setState] = useState(() => loadClimb(uid));
  const [peek, setPeek] = useState(null);           // a rung you tapped (its totem, one tap away)
  useEffect(() => { setState(loadClimb(uid)); return watchClimb(uid, () => setState(loadClimb(uid))); }, [uid]);
  // a run THIS window walked out of mid-bout (or one whose window closed: its mark went stale) counts as that bout lost.
  // Only once the server has answered, never while a result is on its way (busy), never offline.
  const { rec, run, ready, busy, canAct } = state;
  const [, setTick] = useState(0);
  const live = liveState(run);
  useEffect(() => {   // another window's mark goes stale on its own: look again now and then
    if (live !== "elsewhere") return undefined;
    const id = setInterval(() => setTick((n) => n + 1), Math.min(15000, LIVE_STALE_MS / 4));
    return () => clearInterval(id);
  }, [live]);
  const going = useRef(false);   // a bout claimed and on its way into the fight: never "walked out of"
  useEffect(() => {
    if (going.current || !canAct || busy || !run || live !== "here") return;   // "stale" waits for a person (the button below)
    const opp = boutOf(run);
    endWalkedOut(uid, run, (r, rc) => ({ rec: abandon(r, rc).rec, run: null })).then((ok) => ok && onNews &&
      onNews({ kind: "run_over", text: `You left the fight with ${opp ? opp.name : "them"} — that counts as a loss. The run is over.` }));
  }, [uid, canAct, busy, live]);   // eslint-disable-line react-hooks/exhaustive-deps

  const open = new Set(openRungs(rec, L.ladder));
  const myThree = (run && run.team) || team;
  const sit = wouldSit(rec.elo, L.allElos);
  const ready3 = Array.isArray(team) && team.length === 3;

  const begin = (rung) => {
    if (!L.ready || !ready3 || !canAct || run) return;   // the totem is rolled from the real standings, never the loading placeholder
    const at = Date.now();
    beginRun(uid, (rc) => startRun({ rung, entrantOf: L.entrantOf, eloOf: L.eloOf, number1: L.number1, at, team, tomes, rec: rc }));
    setPeek(null);
  };
  // giving up between bouts counts as losing the next one (no banking rating and walking away before a boss)
  const giveUp = () => {
    if (!canAct || busy || live === "elsewhere") return;
    const opp = boutOf(run);
    giveUpRun(uid, run, (r, rc) => ({ rec: abandon(r, rc).rec, run: null })).then((ok) => ok && onNews &&
      onNews({ kind: "run_over", text: `You gave up before ${opp ? opp.name : "the next bout"} — that counts as a loss.` }));
  };
  const endStale = () => {
    if (!canAct || busy || live !== "stale") return;
    const opp = boutOf(run);
    endWalkedOut(uid, run, (r, rc) => ({ rec: abandon(r, rc).rec, run: null })).then((ok) => ok && onNews &&
      onNews({ kind: "run_over", text: `The fight with ${opp ? opp.name : "them"} counts as a loss. The run is over.` }));
  };
  // the bout is CLAIMED for this window first (a server transaction): two windows can never fight the same bout
  const [claiming, setClaiming] = useState(false);
  const fight = async () => {
    if (!canAct || busy || claiming || live === "elsewhere") return;
    setClaiming(true); going.current = true;
    // no timeout on purpose: a dropped connection holds the claim until it's back, and a claim that landed late with
    // nobody waiting for it would read as "walked out" (a loss). The button just says it's waiting.
    const ok = await claimBout(uid, run);
    const now = loadClimb(uid).run;
    if (ok && now && liveState(now) === "here") { onFight(now); return; }   // this screen leaves; `going` stays up
    going.current = false; setClaiming(false);
    onNews && onNews({ kind: "run_over", text: "That bout can't be fought from here right now (another window has it)." });
  };

  const opp = boutOf(run);
  return (
    <div className="screen climb">
      <div className="cl-panel">
        <div className="cl-head">
          <div>
            <h2>The Climb</h2>
            <div className="cl-sub">Through the Contenders, then the Circuit, to the League's #1. Only Anima carries between bouts.</div>
          </div>
          <button className="btn ghost" onClick={onBack}>Back</button>
        </div>
        <div className="cl-rating">
          <b>{Math.round(rec.elo)}</b><span>rating · you'd sit <b>#{sit}</b> of {L.allElos.length} in the League</span>
          {!L.ready && <em>the League's standings are loading…</em>}
          {!ready && <em>getting your Climb…</em>}
          {state.offline && <em>can't reach the server · your Climb is view-only until it's back</em>}
        </div>

        {news && <div className={"cl-news " + news.kind}>{news.text}</div>}

        {run ? (
          <div className="cl-run">
            <div className="cl-run-title">{run.title} <span>bout {run.bout + 1} of {run.totem.length}{run.replay ? " · a replay (practice: no rating, no rung purse)" : ""}</span></div>
            <div className="cl-totem">
              {run.totem.map((t, i) => (
                <div key={t.key} className={"cl-bout" + (i < run.bout ? " done" : i === run.bout ? " now" : "") + (t.boss ? " boss" : "")}>
                  <div className="cl-bout-name">{t.top ? "#1 " : t.boss ? "◆ " : ""}{t.name}</div>
                  <Faces team={t.team} />
                </div>
              ))}
            </div>
            <div className="cl-yours">
              <span>Your three {run.carry ? "walk in carrying" : "start fresh"}</span>
              <Faces team={myThree} carry={run.carry} />
            </div>
            {live === "elsewhere" ? (
              <div className="cl-actions"><span className="cl-lock">This bout is being fought in another window.</span></div>
            ) : live === "stale" ? (
              <div className="cl-actions">
                <span className="cl-lock">This bout was being fought in another window that's gone quiet.</span>
                <button className="btn ghost" disabled={!canAct || busy} onClick={endStale} title="Counts as losing the bout">That window's gone · count it as lost</button>
              </div>
            ) : (
              <div className="cl-actions">
                <button className="btn primary" disabled={!canAct || busy || claiming} onClick={fight}>{claiming ? "Waiting for the server…" : `Fight ${opp ? opp.name : ""} ▸`}</button>
                <button className="btn ghost" disabled={!canAct || busy || claiming} onClick={giveUp} title="Counts as losing the next bout">Give up the run</button>
              </div>
            )}
          </div>
        ) : (
          <div className="cl-ladder">
            {L.ladder.map((r) => {
              const state2 = isCleared(rec, r.index) ? "cleared" : open.has(r.index) ? "open" : "locked";
              const isPeek = peek === r.index;
              return (
                <div key={r.id} className={"cl-rung " + state2 + (isPeek ? " peek" : "")}>
                  <button className="cl-rung-row" disabled={state2 === "locked"} onClick={() => setPeek(isPeek ? null : r.index)}>
                    <span className="cl-rung-mark">{state2 === "cleared" ? "✓" : state2 === "open" ? "▸" : "🔒"}</span>
                    <span className="cl-rung-title">{r.title}</span>
                    <span className="cl-rung-meta">{r.division === "circuit" ? "Circuit" : "Contenders"} · {r.keys.length} bout{r.keys.length === 1 ? "" : "s"}</span>
                  </button>
                  {isPeek && (
                    <div className="cl-peek">
                      <div className="cl-totem">
                        {r.keys.map((k, i) => { const e = L.entrantOf(k); return (
                          <div key={k} className={"cl-bout" + (i === r.keys.length - 1 ? " boss" : "")}>
                            <div className="cl-bout-name">{k === L.number1 ? "#1 " : i === r.keys.length - 1 ? "◆ " : ""}{e ? e.name : k}</div>
                            {e && <Faces team={e.team} />}
                          </div>); })}
                      </div>
                      {!ready3 ? <button className="btn primary" onClick={onPickTeam}>Pick your three ▸</button>
                        : !L.ready || !canAct ? <button className="btn primary" disabled>{!ready ? "Getting your Climb…" : state.offline ? "Can't reach the server" : "Waiting for the League…"}</button>
                        : <>
                            <button className="btn primary" onClick={() => begin(r)}>Start with these three ▸</button>
                            <span className="cl-lock">Your three are locked in for the whole rung{state2 === "cleared" ? " · a replay is practice: no rating, no rung purse" : ""}</span>
                            <Faces team={team} />
                          </>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="cl-foot">Every win pays Mini Dust · clearing a rung pays {fmtKrests(CLIMB_RUNG_PURSE)} King's Krest · placeholder names{uid ? "" : " · sign in to keep your Climb everywhere (it's saved in this browser only)"}</div>
      </div>
    </div>
  );
}
